import type { ReactElement } from "react";
import { Link as LinkIcon } from "lucide-react";
import { Badge, EmptyState, Stack, Text } from "@ux/primitives";
import { DataTable, type DataColumn } from "@ux/compositions";
import { formatTimestamp } from "../common/formatting";
import { formatUserDisplay } from "../users/display";
import type { UserDirectoryEntry } from "../users/types";
import type { AssetView } from "../assets/types";
import type { WorkspaceView } from "../workspaces/types";
import type { AssetAssignmentView } from "./types";

export interface AssetAssignmentsListProps {
    readonly rows: readonly AssetAssignmentView[];
    readonly assetIndex: ReadonlyMap<string, AssetView>;
    /**
     * Per-id resolution map (`useWorkspaceDisplayNames`). `null` values
     * mean "still loading" or "not found"; the row renderer treats both
     * the same and falls back to displaying the raw workspace id.
     */
    readonly workspaceIndex: ReadonlyMap<string, WorkspaceView | null>;
    readonly userIndex: ReadonlyMap<string, UserDirectoryEntry | null>;
    readonly selectedId: string | null;
    readonly onSelect: (row: AssetAssignmentView) => void;
    readonly isLoading: boolean;
    readonly error: string | null;
}

export function AssetAssignmentsList({
    rows,
    assetIndex,
    workspaceIndex,
    userIndex,
    selectedId,
    onSelect,
    isLoading,
    error,
}: AssetAssignmentsListProps): ReactElement {
    const columns: readonly DataColumn<AssetAssignmentView>[] = [
        {
            id: "asset",
            header: "Asset",
            render: (row) => {
                const asset = assetIndex.get(row.assetId);
                const primary =
                    asset !== undefined
                        ? (asset.assetTag !== null &&
                          asset.assetTag !== undefined &&
                          asset.assetTag !== ""
                              ? asset.assetTag
                              : `${asset.make} ${asset.model}`)
                        : row.assetId;
                return (
                    <Stack gap={1}>
                        <Text role="body" weight="semibold">
                            {primary}
                        </Text>
                        <Text role="muted">
                            {describeTarget(row, workspaceIndex, userIndex)}
                        </Text>
                    </Stack>
                );
            },
        },
        {
            id: "when",
            header: "Assigned",
            width: "180px",
            render: (row) => <Text role="muted">{formatTimestamp(row.assigned.timeStamp)}</Text>,
        },
        {
            id: "status",
            header: "Status",
            width: "110px",
            render: (row) =>
                row.isOpen ? <Badge tone="accent">Open</Badge> : <Badge tone="neutral">Released</Badge>,
        },
    ];

    if (isLoading && rows.length === 0) {
        return <EmptyState icon={<LinkIcon size={28} />} title="Loading assignments…" />;
    }
    if (error !== null) {
        return (
            <EmptyState
                icon={<LinkIcon size={28} />}
                title="Could not load assignments"
                description={error}
            />
        );
    }

    return (
        <DataTable<AssetAssignmentView>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            selectedKey={selectedId}
            onSelect={onSelect}
            emptyContent={
                <EmptyState
                    icon={<LinkIcon size={28} />}
                    title="No assignments yet"
                    description="Assign an asset to a user or workspace from the toolbar."
                />
            }
        />
    );
}

function describeTarget(
    row: AssetAssignmentView,
    workspaceIndex: ReadonlyMap<string, WorkspaceView | null>,
    userIndex: ReadonlyMap<string, UserDirectoryEntry | null>,
): string {
    if (row.targetKind === "User") {
        const userId = row.assignedToUserId;
        if (userId === undefined || userId === null || userId === "") {
            return "User · —";
        }
        return `User · ${formatUserDisplay(userIndex.get(userId) ?? null, userId)}`;
    }
    if (row.assignedToWorkspaceId !== undefined && row.assignedToWorkspaceId !== null) {
        const ws = workspaceIndex.get(row.assignedToWorkspaceId);
        // Treat undefined (id not in map) and null (resolved 404 / still
        // loading) the same — fall back to the raw id rather than
        // pretending the workspace exists.
        if (ws !== undefined && ws !== null) {
            const friendly =
                ws.friendlyName !== null && ws.friendlyName !== undefined && ws.friendlyName !== ""
                    ? ` (${ws.friendlyName})`
                    : "";
            return `Workspace · ${ws.name}${friendly}`;
        }
        return `Workspace · ${row.assignedToWorkspaceId}`;
    }
    return row.targetKind;
}
